import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

export interface DrawerMessage {
  from: string
  to: string
  action: 'refresh' | 'update' | 'close' | 'validate'
  data?: unknown
}

export interface DrawerConfig {
  id: string
  title: string
  content: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full' | '90' | '80' | '70' | '60' | '50'
  onClose?: () => void
  closeOnOutsideClick?: boolean
  showCloseButton?: boolean
  headerActions?: ReactNode
  
  // Enhanced features
  hasUnsavedChanges?: boolean
  confirmClose?: boolean
  confirmCloseMessage?: string
  autoSave?: boolean
  autoSaveInterval?: number
  validationMessages?: Record<string, string>
  collapsibleSections?: boolean
  showFieldDiff?: boolean
  maxStack?: number
  dependencies?: string[]
  lazyLoad?: boolean
  loadingState?: 'idle' | 'loading' | 'success' | 'error'
  onMessage?: (message: DrawerMessage) => void
}

interface DrawerStackItem extends DrawerConfig {
  zIndex: number
}

interface DrawerContextType {
  drawers: DrawerStackItem[]
  openDrawer: (config: DrawerConfig) => void
  closeDrawer: (id: string) => void
  closeAllDrawers: () => void
  updateDrawer: (id: string, config: Partial<DrawerConfig>) => void
  bringToFront: (id: string) => void
  sendMessage: (message: DrawerMessage) => void
  getDrawer: (id: string) => DrawerStackItem | undefined
}

// eslint-disable-next-line react-refresh/only-export-components
export const DrawerContext = createContext<DrawerContextType | undefined>(undefined)

const MAX_DRAWER_STACK = 5
const BASE_Z_INDEX = 50

// Event bus for drawer messaging
const drawerMessageBus = new EventTarget()

export function DrawerProvider({ children }: { children: ReactNode }) {
  const [drawers, setDrawers] = useState<DrawerStackItem[]>([])

  const openDrawer = useCallback((config: DrawerConfig) => {
    setDrawers(prev => {
      // Check max stack limit
      const maxStack = config.maxStack || MAX_DRAWER_STACK
      if (prev.length >= maxStack) {
        console.warn(`Maximum drawer stack limit (${maxStack}) reached`)
        return prev
      }

      const existingIndex = prev.findIndex(d => d.id === config.id)
      
      if (existingIndex !== -1) {
        // Drawer already exists, bring it to front
        const updated = [...prev]
        const [drawer] = updated.splice(existingIndex, 1)
        drawer.zIndex = BASE_Z_INDEX + updated.length
        updated.push(drawer)
        return updated
      }
      
      // Add new drawer
      const newDrawer: DrawerStackItem = {
        ...config,
        zIndex: BASE_Z_INDEX + prev.length,
        showCloseButton: config.showCloseButton !== false,
        closeOnOutsideClick: config.closeOnOutsideClick !== false,
        loadingState: config.loadingState || 'idle',
      }
      
      return [...prev, newDrawer]
    })
  }, [])

  const closeDrawer = useCallback((id: string) => {
    setDrawers(prev => {
      const drawer = prev.find(d => d.id === id)
      
      if (drawer) {
        // Close dependent drawers
        if (drawer.dependencies) {
          drawer.dependencies.forEach(depId => {
            const depDrawer = prev.find(d => d.id === depId)
            if (depDrawer?.onClose) {
              depDrawer.onClose()
            }
          })
          // Remove drawer and its dependencies
          return prev.filter(d => 
            d.id !== id && !drawer.dependencies?.includes(d.id)
          )
        }
        
        // Call onClose callback
        if (drawer.onClose) {
          drawer.onClose()
        }
      }
      
      return prev.filter(d => d.id !== id)
    })
  }, [])

  const closeAllDrawers = useCallback(() => {
    drawers.forEach(drawer => {
      if (drawer.onClose) {
        drawer.onClose()
      }
    })
    setDrawers([])
  }, [drawers])

  const updateDrawer = useCallback((id: string, config: Partial<DrawerConfig>) => {
    setDrawers(prev => prev.map(drawer => 
      drawer.id === id 
        ? { ...drawer, ...config, zIndex: drawer.zIndex }
        : drawer
    ))
  }, [])

  const bringToFront = useCallback((id: string) => {
    setDrawers(prev => {
      const index = prev.findIndex(d => d.id === id)
      if (index === -1 || index === prev.length - 1) return prev
      
      const updated = [...prev]
      const [drawer] = updated.splice(index, 1)
      drawer.zIndex = BASE_Z_INDEX + updated.length
      updated.push(drawer)
      return updated
    })
  }, [])

  const sendMessage = useCallback((message: DrawerMessage) => {
    const event = new CustomEvent('drawer-message', {
      detail: message
    })
    drawerMessageBus.dispatchEvent(event)
    
    // Also call onMessage handlers if specified
    drawers.forEach(drawer => {
      if ((message.to === drawer.id || message.to === '*') && drawer.onMessage) {
        drawer.onMessage(message)
      }
    })
  }, [drawers])

  const getDrawer = useCallback((id: string) => {
    return drawers.find(d => d.id === id)
  }, [drawers])

  const contextValue: DrawerContextType = {
    drawers,
    openDrawer,
    closeDrawer,
    closeAllDrawers,
    updateDrawer,
    bringToFront,
    sendMessage,
    getDrawer,
  }

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}
    </DrawerContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDrawer() {
  const context = useContext(DrawerContext)
  if (!context) {
    throw new Error('useDrawer must be used within a DrawerProvider')
  }
  return context
}

// Export message bus for external use
// eslint-disable-next-line react-refresh/only-export-components
export { drawerMessageBus }